/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import { isNilOrEmpty, assoc } from '../../../language/module';
import { Select } from './Select';
import { Option } from './Option';
import { ReactKey } from '../../../layout/reactKey';
import { makeString } from '../../../language/strings';
import { getResource } from '../../../language/resources/getResource';
import { SerializedStyles } from '@emotion/react';


/**
 * Dropdown
 * This component takes an array of values and some optional functions and uses them to construct a dropdown box. It
 * includes a callback function for when a selection is changed as well.
 *
 * If you do not supply a key/value/label function, R.toString will be the default. See the R.toString docs for more
 * information.
 *
 * Internally, this component makes heavy use of your `getValue` function -- you'll want to make sure this function is
 * pure (always maps the same input to the same output (computationally consistent) with no side effects)
 */
export class Dropdown<T> extends React.PureComponent<{
  selectedItem?: T;
  items: T[];
  getKey?: (item: T) => ReactKey;
  getLabel?: (item: T) => string;
  getValue?: (item: T) => string | number;
  placeholderMessage?: string;
  noItemsMessage: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onChange: (item: T) => void;
  rootCss?: SerializedStyles;
  placeholderCss?: SerializedStyles;
  title?: string;
  resources: Record<string, any>
}, {
  selectedItem?: T,
}> {
  public static defaultProps = {
    autoFocus: false,
  };

  public state = {
    selectedItem: this.props.selectedItem,
  };


  public componentDidUpdate(prevProps) {
    if (prevProps.selectedItem !== this.props.selectedItem) {
      this.setState(prev => assoc('selectedItem', this.props.selectedItem, prev));
    }
  }


  private getValue = (value: T) => (this.props.getValue || makeString)(value);
  private getKey   = (value: T) => (this.props.getKey || makeString)(value);
  private getLabel = (value: T) => (this.props.getLabel || makeString)(value);


  private onSelectionChange = (event) => this.props.onChange && this.props.onChange(
    R.find(item => this.getValue(item).toString() === event.target.value, this.props.items)!,
  );


  public render = () => {

    const isPlaceholder =
      !this.state.selectedItem ||
      this.getValue(this.state.selectedItem) === '';

    return (
    <Select autoFocus={this.props.autoFocus}
            onChange={this.onSelectionChange}
            value={isPlaceholder ? '' : this.getValue(this.state.selectedItem!)}
            className={isPlaceholder ? 'is-placeholder' : undefined}
            css={this.props.rootCss}
            disabled={this.props.disabled}>

      {/* Empty/Placeholder option */}
      <Option value='' disabled={true} hidden={true}>
        {
          isNilOrEmpty(this.props.items)
          ? this.props.noItemsMessage
          : this.props.placeholderMessage || getResource(this.props.resources, 'DROPDOWN_PLEASE_SELECT')
        }
      </Option>

      { /* Map out items to dropdown options */
        this.props.items.map(
          pass => <Option key={this.getKey(pass)} value={this.getValue(pass)}>{this.getLabel(pass)}</Option>,
        )
      }
    </Select>
    );
  }
}
